import { ArrowLeft, Activity } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import homeService from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";

const LifestyleScoreDetail = () => {
  const navigate = useNavigate();

  // Fetch home page data for the navigated date
  const {
    data: homeData,
    isLoading: loading,
    selectedDate,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get lifestyle score from API
  const lifestyleScore = (homeData as any)?.["Lifestyle Synergy"]?.lifestyle_score || 0;
  const lifestyleLabel = (homeData as any)?.["Lifestyle Synergy"]?.label || "";
  
  // Use backend value directly - NO conversion or calculation
  const maxScore = 100; // Backend sends score out of 100
  const scorePercentage = lifestyleScore; // Direct value for progress bar
  
  // Get status based on score (assuming 0-100 scale from backend)
  const getScoreStatus = (score: number) => {
    if (score >= 80) {
      return { label: "Excellent", color: "text-green-600", bgColor: "bg-green-500" };
    }
    if (score >= 60) {
      return { label: "Good", color: "text-blue-600", bgColor: "bg-blue-500" };
    }
    if (score >= 40) {
      return { label: "Fair", color: "text-yellow-600", bgColor: "bg-yellow-500" };
    }
    return { label: "Needs Improvement", color: "text-red-600", bgColor: "bg-red-500" };
  };

  const status = getScoreStatus(lifestyleScore);

  if (loading) {
    return (
      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-footnote text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title3 font-bold text-foreground">
            {formattedSelectedDate} Lifestyle Score
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Lifestyle Score Card */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-green-50 to-teal-50 p-6 shadow-sm dark:from-green-950 dark:to-teal-950">
          <div className="text-center">
            <p className="mb-4 text-footnote font-medium text-muted-foreground">
              Today's Lifestyle Synergy Score
            </p>
            <div className="mb-2 text-6xl font-bold text-green-600 dark:text-green-400">
              {Math.round(lifestyleScore)}
            </div>
            <p className="mb-2 text-footnote text-muted-foreground">
              of {maxScore} maximum
            </p>
            {lifestyleLabel && (
              <p className={`mb-4 text-footnote font-semibold ${status.color}`}>
                {lifestyleLabel}
              </p>
            )}

            {/* Progress Bar */}
            <div className="mx-auto h-3 w-full overflow-hidden rounded-full bg-green-200 dark:bg-green-900">
              <div
                className={`h-full transition-all duration-500 ${status.bgColor}`}
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Lifestyle Synergy Score */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 text-headline font-bold text-foreground">
            <Activity className="h-5 w-5 text-green-500" />
            What is Lifestyle Synergy Score?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground">
            Lifestyle Synergy Score measures how well your daily habits and fitness 
            activity support overall health, on a scale of 0-100. It's calculated 
            entirely from your Fitbit data, reflecting your physical activity, sleep 
            quality, stress management, and recovery patterns.
          </p>
        </Card>

        {/* Calculated From */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-headline font-bold text-foreground uppercase tracking-wide">
            Calculated From
          </h3>
          <ul className="space-y-3 text-subhead text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <div>
                <span className="font-semibold text-foreground">Activity Score:</span>{" "}
                <span>Steps, active minutes, calories burned</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <div>
                <span className="font-semibold text-foreground">Sleep & Recovery:</span>{" "}
                <span>Sleep duration, HRV, resting HR</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <div>
                <span className="font-semibold text-foreground">Stress & Readiness:</span>{" "}
                <span>Stress score, breathing rate, SpO2</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <div>
                <span className="font-semibold text-foreground">Metabolic Stability:</span>{" "}
                <span>Heart rate variability, sleep quality, and temperature variation</span>
              </div>
            </li>
          </ul>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-headline font-bold text-foreground">
            Score Ranges
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-callout font-medium text-foreground">
                Excellent
              </span>
              <span className="text-callout font-semibold text-green-600">
                80-100
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-callout font-medium text-foreground">Good</span>
              <span className="text-callout font-semibold text-blue-600">60-79</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-callout font-medium text-foreground">Fair</span>
              <span className="text-callout font-semibold text-yellow-600">
                40-59
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-callout font-medium text-foreground">
                Needs Improvement
              </span>
              <span className="text-callout font-semibold text-red-600">0-39</span>
            </div>
          </div>
        </Card>

        {/* Ways to Improve */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-headline font-bold text-foreground">
            Ways to Improve
          </h3>
          <ul className="space-y-3 text-callout text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <span>Aim for 7-9 hours of quality sleep each night</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <span>Include at least 30 minutes of moderate exercise daily</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <span>
                Practice stress management through meditation or breathing exercises
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-green-500">•</span>
              <span>Maintain consistent meal and sleep schedules</span>
            </li>
          </ul>
        </Card>
      </div>

      <BottomNavBar activeIndex={0} />
    </div>
  );
};

export default LifestyleScoreDetail;
